/**
 * Organization chooser inside an authorization flow (PRD §7.5, §8.2).
 *
 * `/authorize` sends the user here when it cannot decide which organization to
 * authorize — several active memberships and no stored preference, or a module
 * that asked for `prompt=select_account`. Choosing sets the session's active
 * organization and hands control straight back, so the user makes one choice
 * and lands in the module.
 *
 * Distinct from the account-area switcher (`select-organization.tsx`), which is
 * a settings screen: this one is a step in a flow and has somewhere to return
 * to. It also uses the unauthenticated shell, because it sits between sign-in
 * and the module rather than inside the account.
 *
 * The choice is only ever a *request*: the server re-checks membership and
 * re-runs the entitlement decision on the way back through `/authorize`, so a
 * tampered organization id gains nothing (PRD §16 tenant isolation).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { MembershipSummary } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkButton, ModuleBanner, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useAuthRequest } from '@/hooks/use-auth-request';
import { ApiError, api } from '@/lib/api';

export function ChooseOrganizationPage() {
  const { user, loading } = useAuth();
  const authRequest = useAuthRequest();
  const [memberships, setMemberships] = useState<MembershipSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { memberships: items } = await api.memberships();
        setMemberships(items.filter((membership) => membership.status === 'active'));
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'Could not load your organizations.',
        );
        setMemberships([]);
      }
    })();
  }, []);

  const choose = useCallback(
    (organizationId: string) => {
      setChoosing(organizationId);
      setError(null);
      void (async () => {
        try {
          await api.setActiveOrganization(organizationId);
          // Back to /authorize, which validates the choice and issues the code.
          if (authRequest.resume()) return;
          setError('This sign-in request has expired. Return to the application and try again.');
        } catch (caught) {
          setError(
            caught instanceof ApiError ? caught.message : 'Could not select that organization.',
          );
        } finally {
          setChoosing(null);
        }
      })();
    },
    [authRequest],
  );

  // Reached without a flow — there is nothing to return to, so send the user to
  // the account switcher, which is the same choice in its settings form.
  if (!authRequest.handle) return <Navigate to="/account/organizations" replace />;

  if (loading) {
    return (
      <AuthLayout title="Choose an organization">
        <div className="flex items-center gap-3 text-sm text-slate-400" role="status">
          <Spinner className="size-5 text-brand-600" />
          Loading…
        </div>
      </AuthLayout>
    );
  }

  if (!user) return <Navigate to={authRequest.link('/sign-in')} replace />;

  return (
    <AuthLayout
      title="Choose an organization"
      description={
        authRequest.applicationName
          ? `You belong to more than one organization. Pick the one to use in ${authRequest.applicationName}.`
          : 'You belong to more than one organization. Pick the one to use.'
      }
      footer={
        <Link to="/sign-out" className="font-medium text-brand-400 hover:text-brand-300">
          Sign in as someone else
        </Link>
      }
    >
      <ModuleBanner applicationName={authRequest.applicationName} />

      <div className="space-y-4">
        {error && <DarkAlert tone="error">{error}</DarkAlert>}

        {memberships === null ? (
          <div className="flex items-center gap-3 text-sm text-slate-400" role="status">
            <Spinner className="size-5 text-brand-600" />
            Loading your organizations…
          </div>
        ) : memberships.length === 0 ? (
          <DarkAlert tone="warning">
            You&apos;re not an active member of any organization, so no module can be opened yet.
          </DarkAlert>
        ) : (
          <ul className="space-y-3">
            {memberships.map((membership) => (
              <li key={membership.organization.id}>
                <DarkButton
                  loading={choosing === membership.organization.id}
                  disabled={choosing !== null}
                  onClick={() => choose(membership.organization.id)}
                  className="justify-between text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {membership.organization.name}
                    </span>
                    <span className="block truncate text-xs text-slate-400">
                      {membership.roles.length > 0 ? membership.roles.join(', ') : 'member'}
                      {membership.organization.type === 'global' && ' · Global organization'}
                    </span>
                  </span>
                </DarkButton>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AuthLayout>
  );
}
